import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { ProductIds, UserClassAssignment, getUsersFromExistingProduct, importUsersFromOtherProduct } from 'core'

import { Alert, Button, Card, Col, Divider, Icon, Notification, Row, Skeleton } from 'common/components'
import { useCommandRequestV2, useLoginUserV2, useQueryRequestV2 } from 'common/hooks'
import { ProductConfig } from 'common/providers/ProductProvider'
import { ROUTE_PATHS } from 'common/routePaths'

import ImportEmployeesTable from './ImportEmployeesTable'

const ImportUsers = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const { user } = useLoginUserV2()

  const {
    data: employeesInfo,
    isLoading: isEmployeeLoading,
    reload: reloadEmployees,
  } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getUsersFromExistingProduct(),
  })
  const { sendCommand: sendImportUsersFromOtherProducts, isLoading: isImportUsersSubmitLoading } = useCommandRequestV2(
    (values: UserClassAssignment[]) => importUsersFromOtherProduct({ companyUserInputs: values }),
  )

  const [submitList, setSubmitList] = useState<UserClassAssignment[]>([])

  const unAssignedEmployeesCount = employeesInfo?.usersToImport.length - submitList.length

  const handleAddToSubmitList = (id: string, value: string | null) => {
    setSubmitList((prev) => {
      const newList = [
        ...prev.filter((item) => item.companyUserId !== id),
        { companyUserId: id, productClassId: value },
      ]
      return newList.filter((item) => item.productClassId !== null)
    })
  }

  const handleSubmit = async () => {
    if (submitList.length !== 0) {
      const result = await sendImportUsersFromOtherProducts(submitList)
      if (result.completed) {
        const totalRequested = result.data?.totalRequested
        const totalSucceeded = result.data?.totalSucceeded
        const totalFailed = totalRequested - totalSucceeded

        if (totalFailed > 0) {
          Notification.error(`${totalFailed}/${totalRequested} employees failed to sync. Please try again.`)
        } else {
          Notification.success('Employees have been synced successfully!')
        }
        reloadEmployees()
        setSubmitList([])
        history.push(`${ROUTE_PATHS.MANAGE_PEOPLE}`)
      }
    } else history.push(`${ROUTE_PATHS.MANAGE_PEOPLE}`)
  }

  const submitButton = (
    <Button type="primary" loading={isImportUsersSubmitLoading} onClick={handleSubmit}>
      Continue
    </Button>
  )

  const description = () => {
    switch (user.productId) {
      case ProductIds.Perks:
        return (
          <Row>
            <Col sm={24} lg={17}>
              <h5 className="mb-16">Assign existing employees to a benefit class</h5>
              <p className="color-gray-8 mb-16">
                Here, you can add employees from your other PeopleKeep benefits to ensure that all your benefit-eligible
                employees have a WorkPerks class assignment.
              </p>
              <p className="color-gray-8 mb-16">
                You can continue to the next page if you would like to add additional employees or 1099 contractors that
                are not included in your other PeopleKeep benefits.
              </p>
            </Col>
          </Row>
        )
      case ProductIds.IchraId:
        return (
          <Row>
            <Col sm={24} lg={17}>
              <h5 className="mb-16">Assign existing employees to a benefit class</h5>
              <p className="color-gray-8 mb-16">
                Here, you can add employees from your other PeopleKeep benefits to ensure that all your benefit-eligible
                employees have an ICHRA class assignment.
              </p>
              <p className="color-gray-8 mb-16">
                You can continue to the next page if you would like to add additional employees who are not included in
                your other PeopleKeep benefits.
              </p>
            </Col>
          </Row>
        )
      case ProductIds.QsehraId:
        return (
          <Row>
            <Col sm={24} lg={17}>
              <h5 className="mb-16">Assign existing employees to a benefit class</h5>
              <p className="color-gray-8 mb-16">
                Here, you can add employees from your other PeopleKeep benefits to ensure that all your benefit-eligible
                employees have a QSEHRA class assignment.
              </p>
              <p className="color-gray-8 mb-16">
                You can continue to the next page if you would like to add additional employees who are not included in
                your other PeopleKeep benefits.
              </p>
            </Col>
          </Row>
        )
      default:
        return (
          <Row>
            <Col sm={24} lg={17}>
              <h5 className="mb-16">Assign existing employees to a benefit class</h5>
              <p className="color-gray-8 mb-16">
                Here, you can add employees from your other PeopleKeep benefits to ensure that all your benefit-eligible
                employees have a GCHRA class assignment.
              </p>
              <p className="color-gray-8 mb-16">
                You can continue to the next page if you would like to add additional employees who are not included in
                your other PeopleKeep benefits.
              </p>
            </Col>
          </Row>
        )
    }
  }

  return (
    <Card title="Sync employees">
      {description()}
      {unAssignedEmployeesCount > 0 && (
        <Alert
          showIcon
          icon={<Icon fontAwesome type="fas fa-exclamation-triangle" className="color-yellow-7 mr-8" />}
          type="warning"
          className="mb-16"
          message={
            <div className="mb-4">
              You have {unAssignedEmployeesCount} employees “Unassigned” to a{' '}
              {ProductConfig.get(user.productId).productName} class.
            </div>
          }
        />
      )}
      <Skeleton loading={isEmployeeLoading}>
        {employeesInfo && (
          <Card title="Eligible Employees">
            <ImportEmployeesTable
              employees={employeesInfo.usersToImport}
              productClasses={employeesInfo.productClasses}
              onAddToSubmitList={handleAddToSubmitList}
              submitList={submitList}
            />
          </Card>
        )}
        <Divider className="mb-16 mt-32 -mx-24 w-auto" />
        <div className="flex flex__end">{submitButton}</div>
      </Skeleton>
    </Card>
  )
}

export default ImportUsers
